import React, { useState, useMemo } from "react";
import _ from "lodash";
import { makeStyles } from "@mui/styles";
import {
  Box,
  Collapse,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  Paper,
  TextField,
} from "@mui/material";
import { useLocation } from "react-router-dom";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";
import { Autocomplete } from "@mui/material";
import ResponsiveDrawer from "views/components/ResponsiveDrawer";
import PharmacologyPanel from "views/components/PharmacologyPanel";
import AllKampoData from "data/all-kampo.json";
import TsumuraKampoData from "data/tsumura-kampo.json";
import { matchesKanaSearch } from "utils/kanaUtils";
import { convertHerbArrayToKatakana } from "utils/herbMapping";
import { getTsumuraArrayToKampoDBIds } from "utils/kampodbMapping";

const useRowStyles = makeStyles({
  root: {
    "& > *": {
      borderBottom: "unset",
    },
  },
});

function Row(props) {
  const { row } = props;
  const [open, setOpen] = React.useState(false);
  const classes = useRowStyles();

  // 総生薬数（重複なし）を計算
  const uniqueHerbCount = React.useMemo(() => {
    if (row.length === 1) {
      return row[0].set.replace(/<[^>]*>/g, '').split(',').length;
    }
    // 2つの漢方の生薬をユニオン（重複除去）
    const herbs1 = row[0].set.replace(/<[^>]*>/g, '').split(',').map(h => h.trim());
    const herbs2 = row[1].set.replace(/<[^>]*>/g, '').split(',').map(h => h.trim());
    return _.union(herbs1, herbs2).length;
  }, [row]);

  // KampoDB IDsを取得
  const kampoDBIds = React.useMemo(() => {
    const names = row.map(item => item.name);
    return getTsumuraArrayToKampoDBIds(names);
  }, [row]);

  return (
    <>
      <TableRow className={classes.root}>
        <TableCell>
          <IconButton size="small" onClick={() => setOpen(!open)}>
            {open ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
          </IconButton>
        </TableCell>
        <TableCell component="th" scope="row">
          {row.length > 1 
            ? `${row[0].name} + ${row[1].name} (総生薬数: ${uniqueHerbCount})`
            : `${row[0].name} (総生薬数: ${uniqueHerbCount})`}
        </TableCell>
      </TableRow>
      <TableRow>
        <TableCell style={{ paddingBottom: 0, paddingTop: 0 }} colSpan={6}>
          <Collapse in={open} timeout="auto" unmountOnExit>
            <Box margin={1}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>製品No.</TableCell>
                    <TableCell>名前</TableCell>
                    <TableCell>カナ</TableCell>
                    <TableCell>生薬</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {row.map((item) => (
                    <TableRow key={item.product_no}>
                      <TableCell>{item.product_no}</TableCell>
                      <TableCell>{item.name}</TableCell>
                      <TableCell>{item.kana}</TableCell>
                      <TableCell>
                        <div dangerouslySetInnerHTML={{ __html: item.set }} />
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
                <TableBody>
                  <TableRow>
                    <TableCell colSpan={4} style={{ padding: 0, border: 'none' }}>
                      {kampoDBIds.length > 0 && (
                        <PharmacologyPanel tsumuraKampoIds={kampoDBIds} />
                      )}
                    </TableCell>
                  </TableRow>
                </TableBody>
              </Table>
            </Box>
          </Collapse>
        </TableCell>
      </TableRow>
    </>
  );
}

// ツムラデータで再現可能かチェックする関数（完全一致は除外）
const canReproduceWithTsumura = (targetSyoyakuKanji) => {
  // 漢字の生薬名をカタカナに変換
  const targetSyoyaku = convertHerbArrayToKatakana(targetSyoyakuKanji);
  
  // 2つ以上の組み合わせで再現可能かチェック
  const hashTable = [];
  
  for (let i = 0; i < TsumuraKampoData.length; i++) {
    const currentSyoyaku = TsumuraKampoData[i].set.split(",");
    const diff = _.difference(targetSyoyaku, currentSyoyaku);
    
    if (diff.length === 0) continue; // 完全一致は上で処理済み
    
    // 既存のデータとの組み合わせをチェック
    for (let j = 0; j < hashTable.length; j++) {
      const combined = _.union(currentSyoyaku, hashTable[j]);
      const combinedDiff = _.difference(targetSyoyaku, combined);
      
      if (combinedDiff.length === 0) {
        return true; // 再現可能
      }
    }
    
    hashTable.push(currentSyoyaku);
  }
  
  return false; // 再現不可能
};

function ReproduceSearch() {
  const params = new URLSearchParams(useLocation().search);
  const kampo = params.get("q");
  const [selectedKampo, setSelectedKampo] = useState();
  const [reproduceTsumuraKampo, setReproduceTsumuraKampo] = useState();
  const [searched, setSearched] = useState();
  
  // 事前にツムラデータで再現可能な漢方のみをフィルタリング
  const availableKampoData = useMemo(() => {
    return AllKampoData.filter((kampo) => {
      const targetSyoyaku = kampo.set.split(",");
      return canReproduceWithTsumura(targetSyoyaku);
    });
  }, []);
  const resetStates = () => {
    setSelectedKampo();
    setReproduceTsumuraKampo();
  };
  const handleChange = (_, data) => {
    const selectedKampoName = data?.name;
    if (selectedKampoName) {
      window.location.href = `/reproduce-search?q=${selectedKampoName}`;
    } else {
      resetStates();
    }
  };
  const search = (selectedKampoName) => {
    const selectedKampoData = AllKampoData.find(
      (item) => item.name === selectedKampoName
    );
    if (!selectedKampoData) return;
    setSelectedKampo(selectedKampoData);
    
    // ターゲットの生薬セット（all-kampoから）- 漢字
    const targetSyoyakuKanji = selectedKampoData.set.split(",");
    // カタカナに変換してツムラデータと比較
    const targetSyoyaku = convertHerbArrayToKatakana(targetSyoyakuKanji);
    
    // ツムラデータの組み合わせ（2つ以上）で再現を試みる
    const pairs = [];
    const hashTable = [];
    
    TsumuraKampoData.forEach((tsumuraKampo, currentIndex) => {
      const currentSyoyaku = tsumuraKampo.set.split(",");
      const diff = _.difference(targetSyoyaku, currentSyoyaku);
      
      // 完全に含まれる場合はスキップ（完全一致は上で処理済み）
      if (diff.length === 0) return;
      
      // 差分が空でない場合、その差分を補完できる既存のツムラデータを探す
      hashTable.forEach((prevSyoyaku, prevIndex) => {
        // 2つのツムラデータの組み合わせで、ターゲットを完全に再現できるか確認
        const combined = _.union(currentSyoyaku, prevSyoyaku);
        const combinedDiff = _.difference(targetSyoyaku, combined);
        
        // 完全に再現できる場合のみペアとして追加
        if (combinedDiff.length === 0) {
          // 重複を含まないか確認（全てのターゲット生薬が含まれること）
          const copiedCurrent = Object.assign({}, tsumuraKampo);
          const copiedPrev = Object.assign({}, TsumuraKampoData[prevIndex]);
          
          copiedCurrent.set = copiedCurrent.set
            .split(",")
            .map((item) => {
              if (targetSyoyaku.includes(item)) return `<b>${item}</b>`;
              return item;
            })
            .join(", ");
          
          copiedPrev.set = copiedPrev.set
            .split(",")
            .map((item) => {
              if (targetSyoyaku.includes(item)) return `<b>${item}</b>`;
              return item;
            })
            .join(", ");
          
          pairs.push([copiedPrev, copiedCurrent]);
        }
      });
      
      hashTable.push(currentSyoyaku);
    });
    
    // 総生薬数（重複なし）でソート（少ない順）
    pairs.sort((a, b) => {
      // 各ペアのユニーク生薬数を計算（重複除去）
      const herbsA1 = a[0].set.replace(/<[^>]*>/g, '').split(',').map(h => h.trim());
      const herbsA2 = a[1].set.replace(/<[^>]*>/g, '').split(',').map(h => h.trim());
      const uniqueCountA = _.union(herbsA1, herbsA2).length;
      
      const herbsB1 = b[0].set.replace(/<[^>]*>/g, '').split(',').map(h => h.trim());
      const herbsB2 = b[1].set.replace(/<[^>]*>/g, '').split(',').map(h => h.trim());
      const uniqueCountB = _.union(herbsB1, herbsB2).length;
      
      return uniqueCountA - uniqueCountB;
    });
    
    setReproduceTsumuraKampo(pairs);
  };
  if (kampo && !searched) {
    setSearched(true);
    search(kampo);
  }
  return (
    <ResponsiveDrawer>
      <div style={{ maxWidth: "calc(100vw - 48px)" }}>
        <Typography paragraph>組み合わせ再現漢方検索</Typography>
        <Autocomplete
          autoSelect
          options={availableKampoData}
          getOptionLabel={(option) => option.kana ? `${option.name}（${option.kana}）` : option.name}
          filterOptions={(options, { inputValue }) => {
            if (!inputValue) return options;
            return options.filter((option) =>
              matchesKanaSearch(inputValue, option.name, option.kana)
            );
          }}
          onChange={handleChange}
          renderInput={(params) => (
            <TextField
              {...params}
              label="漢方方剤（ひらがな・カタカナで検索可）"
              variant="outlined"
              placeholder="例: あんちゅうさん、アンチュウサン"
            />
          )}
          defaultValue={selectedKampo}
        />
        {selectedKampo && (
          <>
            <Box mb={2} />
            <Typography paragraph>
              使用されている生薬:{" "}
              <b>{selectedKampo.set.split(",").join(", ")}</b>
            </Typography>
            <Box md={3} />
            {reproduceTsumuraKampo && reproduceTsumuraKampo.length > 0 && (
              <TableContainer component={Paper}>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell />
                      <TableCell>ツムラ組み合わせ漢方名</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {reproduceTsumuraKampo.map((row, index) => (
                      <Row 
                        key={`match-${index}`} 
                        row={row}
                      />
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </>
        )}
      </div>
    </ResponsiveDrawer>
  );
}

export default ReproduceSearch;
